import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, MapPin, DollarSign, Clock, ExternalLink, Search } from 'lucide-react';
import jobAPI from '../services/jobAPI';

const JobListingsSection = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [location, setLocation] = useState('');
  const [apiError, setApiError] = useState(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    let cancelled = false;

    const fetchJobs = async () => {
      setLoading(true);
      setApiError(null);
      try {
        const query = searchTerm.trim() || 'jobs hiring now';
        const fetchedJobs = await jobAPI.searchJobs({
          query,
          location: location.trim() || '',
          numPages: 1,
          datePosted: 'month',
          jobType: 'fulltime'
        });
        
        if (cancelled) return;
        if (fetchedJobs && fetchedJobs.length > 0) {
          setJobs(fetchedJobs);
        } else {
          setJobs([]);
          setApiError('No jobs found matching your search criteria. Try different keywords or location.');
        }
      } catch (error) {
        if (cancelled) return;
        if (error.message && error.message.includes('API key')) {
          setApiError('JSearch API key not configured. See JSEARCH_SETUP.md for setup instructions. Using demo data.');
        } else {
          setApiError('Failed to fetch jobs. Using demo data.');
        }
        try {
          const mockJobs = await jobAPI.searchJobs({ query: 'software engineer' });
          if (!cancelled && mockJobs?.length > 0) setJobs(mockJobs);
        } catch { /* ignore */ }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchJobs();
    } else {
      const timeoutId = setTimeout(fetchJobs, 600);
      return () => { cancelled = true; clearTimeout(timeoutId); };
    }

    return () => { cancelled = true; };
  }, [searchTerm, location]);

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.company.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLocation = !location || job.location.toLowerCase().includes(location.toLowerCase());
    return matchesSearch && matchesLocation;
  });

  return (
    <section className="py-20 bg-gray-50">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Current Job Listings
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Browse the latest job opportunities. Find your next career move with our curated job listings.
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8 bg-white rounded-lg shadow-md p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
              />
            </div>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Location (e.g., Remote, NYC)"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="input pl-10"
              />
            </div>
            <button 
              className="btn btn-primary w-full"
              onClick={() => {
                // Trigger search by updating state (useEffect will handle the API call)
                setSearchTerm(searchTerm);
                setLocation(location);
              }}
            >
              Search Jobs
            </button>
          </div>
        </div>

        {/* API Error Message */}
        {apiError && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">{apiError}</p>
            {apiError.includes('API key') && (
              <p className="text-xs text-yellow-700 mt-2">
                To fetch real jobs, add <code className="bg-yellow-100 px-1 rounded">VITE_JSEARCH_API_KEY</code> to your <code className="bg-yellow-100 px-1 rounded">.env</code> file. See <code className="bg-yellow-100 px-1 rounded">JSEARCH_SETUP.md</code> for instructions.
              </p>
            )}
          </div>
        )}

        {/* Job Listings */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading job listings...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredJobs.length > 0 ? (
              filteredJobs.map((job) => (
                <div
                  key={job.id}
                  className="card hover:shadow-lg transition-all duration-300 hover:border-primary-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 mb-1">{job.title}</h3>
                      <p className="text-lg text-primary-600 font-medium mb-2">{job.company}</p>
                    </div>
                    <Briefcase className="w-6 h-6 text-primary-600 flex-shrink-0" />
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm">{job.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <DollarSign className="w-4 h-4" />
                      <span className="text-sm">{job.salary}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">{job.type} • {job.posted}</span>
                    </div>
                  </div>

                  <Link
                    to={`/jobs/${job.id}`}
                    className="btn btn-outline w-full inline-flex items-center justify-center space-x-2"
                  >
                    <span>View Details</span>
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-600">No jobs found matching your criteria.</p>
              </div>
            )}
          </div>
        )}

        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            Job listings are updated regularly from multiple sources
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500">
            <span>✓ Free to browse</span>
            <span>✓ Multiple sources</span>
            <span>✓ Updated daily</span>
            <span>✓ Apply directly</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JobListingsSection;
